<template>
  <section
    class="studio-dashboard"
    :class="{ 'studio-dashboard--menu-driven': menuDriven }"
    data-testid="studio-dashboard"
  >
    <!-- Desktop keeps tree + actions in the unified AnalysisSideMenu; on
         mobile we show this workspace panel inline so the upper-right app
         drawer can stay the only menu. -->
    <aside class="studio-side" :aria-hidden="menuDriven ? 'true' : undefined">
      <div v-if="isMobileStudio" class="studio-side-head">
        <div class="studio-side-summary">
          <div class="studio-side-title">{{ t('analysis.studio.nav.workspace') }}</div>
          <strong class="studio-side-current-title">
            {{ currentWorkspaceNode ? nodeLabel(currentWorkspaceNode) : t('analysis.studio.currentLabel') }}
          </strong>
          <span class="studio-side-current-meta">
            {{ workspaceSubtitle }}
          </span>
        </div>
      </div>
      <div v-else class="studio-side-title">{{ t('analysis.studio.nav.workspace') }}</div>

      <div class="node-actions" :class="{ 'node-actions--mobile': isMobileStudio }">
        <button
          v-if="isMobileStudio && rootNode && currentWorkspaceNode?.id !== rootNode.id"
          class="node-action"
          type="button"
          data-testid="studio-root-action"
          :disabled="treeBusy"
          @click="onSelectNode(rootNode)"
        >
          <v-icon icon="mdi-floor-plan" size="18" />
          <span>{{ t('analysis.studio.originalLabel') }}</span>
        </button>
        <button
          v-if="hasPremiumAccess && canExportBranch"
          class="node-action"
          type="button"
          :data-testid="isMobileStudio ? 'studio-export-action-mobile' : 'studio-export-action'"
          :disabled="treeBusy || exportBusy"
          @click="$emit('export-branch')"
        >
          <v-icon icon="mdi-file-pdf-box" size="18" />
          <span>{{ t('analysis.studio.nav.exportBranch') }}</span>
        </button>
      </div>

      <div class="node-list" data-testid="studio-tree-list">
        <template v-if="!showFullTree && treeDisplay.condensed">
          <!-- Hierarchical view (default): ancestors of current -> current
               -> immediate children, with indentation showing depth and
               a sibling-count chip when applicable. v2 spec §3.3. -->
          <button
            v-for="entry in treeDisplay.entries"
            :key="entry.node.id"
            type="button"
            class="node-item"
            :class="[
              entry.node.id === currentNodeId ? 'node-item--active' : null,
              entry.node.parentNodeId === null ? 'node-item--root' : null,
              entry.role ? `node-item--${entry.role}` : null,
            ]"
            :data-node-id="entry.node.id"
            :data-testid="`studio-node-${entry.node.id}`"
            :data-role="entry.role"
            :aria-current="entry.node.id === currentNodeId ? 'true' : undefined"
            :disabled="treeBusy"
            :style="{ paddingLeft: `${10 + entry.indent * 14}px` }"
            @click="onSelectNode(entry.node)"
          >
            <span v-if="entry.indent > 0" class="node-connector" aria-hidden="true" />
            <span class="node-thumb" aria-hidden="true">
              <img v-if="entry.node.thumbnailUrl || entry.node.imageUrl" :src="entry.node.thumbnailUrl || entry.node.imageUrl" alt="" />
              <v-icon v-else icon="mdi-floor-plan" size="18" />
            </span>
            <span class="node-label">
              <span class="node-title">{{ nodeLabel(entry.node) }}</span>
              <span v-if="entry.node.archetype" class="node-meta">{{ archetypeLabel(entry.node.archetype) }}</span>
            </span>
            <span v-if="entry.siblingCount > 0" class="node-sibling-chip" :title="t('analysis.studio.nav.siblingTooltip', { count: entry.siblingCount })">
              +{{ entry.siblingCount }}
            </span>
          </button>
        </template>
        <template v-else>
          <button
            v-for="node in orderedNodes"
            :key="node.id"
            type="button"
            class="node-item"
            :class="{ 'node-item--active': node.id === currentNodeId, 'node-item--root': node.parentNodeId === null }"
            :data-node-id="node.id"
            :data-testid="`studio-node-${node.id}`"
            :aria-current="node.id === currentNodeId ? 'true' : undefined"
            :disabled="treeBusy"
            :style="{ paddingLeft: `${10 + Math.min(node.depth || 0, 5) * 14}px` }"
            @click="onSelectNode(node)"
          >
            <span v-if="(node.depth || 0) > 0" class="node-connector" aria-hidden="true" />
            <span class="node-thumb" aria-hidden="true">
              <img v-if="node.thumbnailUrl || node.imageUrl" :src="node.thumbnailUrl || node.imageUrl" alt="" />
              <v-icon v-else icon="mdi-floor-plan" size="18" />
            </span>
            <span class="node-label">
              <span class="node-title">{{ nodeLabel(node) }}</span>
              <span v-if="node.archetype" class="node-meta">{{ archetypeLabel(node.archetype) }}</span>
            </span>
          </button>
        </template>
        <div v-if="orderedNodes.length === 0" class="node-empty">
          {{ t('analysis.studio.nav.emptyTree') }}
        </div>
        <button
          v-if="treeDisplay.condensed"
          type="button"
          class="node-tree-toggle"
          data-testid="studio-tree-toggle"
          @click="showFullTree = !showFullTree"
        >
          <v-icon :icon="showFullTree ? 'mdi-unfold-less-horizontal' : 'mdi-file-tree-outline'" size="14" />
          {{ showFullTree ? t('analysis.studio.nav.collapseTree') : t('analysis.studio.nav.expandTree', { count: orderedNodes.length }) }}
        </button>
      </div>

    </aside>

    <div class="studio-main">
      <div class="studio-hero">
        <div>
          <p class="eyebrow">{{ heroEyebrow }}</p>
          <h2>{{ heroTitle }}</h2>
          <p class="studio-hero-lede">{{ heroLede }}</p>
        </div>
        <div v-if="!isMobileStudio" class="studio-credit-pill">
          <v-icon icon="mdi-circle-multiple" size="18" />
          {{ t('analysis.studio.stepCost', { count: hub.studioStepCredits }) }}
        </div>
      </div>

      <div v-if="driftHintVisible" class="drift-hint" data-testid="studio-drift-hint" role="alert">
        <v-icon icon="mdi-alert-circle-outline" size="20" />
        <span>{{ t('analysis.studio.driftHint') }}</span>
        <v-btn variant="text" size="small" @click="$emit('dismiss-drift')">{{ t('analysis.studio.driftDismiss') }}</v-btn>
      </div>

      <section v-if="stubMode" class="stub-banner" role="note" data-testid="studio-stub-banner">
        <v-icon icon="mdi-flask-outline" size="18" />
        {{ t('analysis.studio.stubBanner') }}
      </section>

      <section
        ref="conceptStageRef"
        class="concept-stage"
        :class="{ 'concept-stage--single': singleConceptPane }"
        data-testid="studio-concept-stage"
      >
        <div class="concept-stage-grid">
          <figure class="concept-pane concept-pane--original">
            <figcaption class="concept-pane-label">
              <v-icon icon="mdi-floor-plan" size="14" />
              <span>{{ t('analysis.studio.originalLabel') }}</span>
            </figcaption>
            <div class="concept-frame">
              <img v-if="imageUrl" :src="imageUrl" alt="" />
              <div v-else class="concept-empty">
                <v-icon icon="mdi-floor-plan" size="42" />
              </div>
            </div>
          </figure>

          <figure v-if="!singleConceptPane" class="concept-pane concept-pane--current">
            <figcaption class="concept-pane-label">
              <span class="concept-pane-label-main">
                <v-icon icon="mdi-auto-fix" size="14" />
                <span>{{ t('analysis.studio.currentLabel') }}</span>
              </span>
              <span v-if="activeNode && activeNode.parentNodeId" class="concept-pane-archetype">
                {{ archetypeLabel(activeNode.archetype) }}
              </span>
            </figcaption>
            <div class="concept-frame">
              <template v-if="pendingGeneration">
                <div class="concept-generating">
                  <v-progress-circular indeterminate color="primary" size="36" width="3" />
                  <span class="concept-generating-eta">{{ t('analysis.studio.generating') }}</span>
                  <v-btn
                    variant="text"
                    size="small"
                    color="primary"
                    data-testid="studio-cancel-generation"
                    @click="$emit('cancel-generation')"
                  >
                    <v-icon icon="mdi-close-circle-outline" start />
                    {{ t('analysis.studio.cancel') }}
                  </v-btn>
                </div>
              </template>
              <template v-else-if="activeNode && activeNode.imageUrl">
                <img :src="activeNode.imageUrl" alt="" />
              </template>
              <div v-else class="concept-empty concept-empty--placeholder">
                <v-icon icon="mdi-image-plus-outline" size="34" />
                <p>{{ t('analysis.studio.currentEmpty') }}</p>
              </div>
            </div>
          </figure>
        </div>

        <figcaption
          v-if="conceptCaption"
          class="concept-stage-caption"
          data-testid="studio-concept-caption"
        >
          <v-icon icon="mdi-cursor-text" size="16" />
          <span>{{ conceptCaption }}</span>
        </figcaption>

        <nav v-if="conceptTrail.length" class="concept-stage-trail" data-testid="studio-cumulative">
          <span class="concept-trail-label">{{ t('analysis.studio.cumulativeLabel') }}</span>
          <ol class="concept-trail-steps">
            <li v-for="(step, i) in conceptTrail" :key="`trail-${i}`" class="concept-trail-step">
              <span class="concept-trail-index">{{ i + 1 }}</span>
              <span class="concept-trail-text" :title="step">{{ step }}</span>
            </li>
          </ol>
        </nav>
      </section>

      <p
        v-if="treeCapStatus.blocked && hasPremiumAccess"
        class="cap-warning"
        role="status"
        data-testid="studio-cap-warning"
      >
        <v-icon icon="mdi-information-outline" size="18" />
        {{ treeCapTooltip }}
      </p>

      <!-- Sticky mobile credit bar: a thin status strip reminding the
        user of their balance. The step cost is already shown on the
        hero credit pill and inside each Execute button, so we don't
        repeat it here. Hidden on desktop. -->
      <div class="studio-mobile-bar" role="status" data-testid="studio-mobile-bar">
        <span class="studio-mobile-bar-pill">
          <v-icon icon="mdi-circle-multiple" size="14" />
          {{ t('analysis.productHub.credits', { count: hub.credits || 0 }) }}
        </span>
        <span class="studio-mobile-bar-cost">
          <v-icon icon="mdi-flash-outline" size="14" />
          {{ t('analysis.studio.stepCost', { count: hub.studioStepCredits }) }}
        </span>
      </div>

      <section class="starter-pack" data-testid="studio-starter-pack">
        <header class="starter-pack-header">
          <div class="starter-pack-kicker">
            <v-icon icon="mdi-target" size="18" />
            <span>{{ t('analysis.studio.starterPack.kicker') }}</span>
          </div>
          <h3 class="starter-pack-title">{{ t('analysis.studio.starterPack.title') }}</h3>
          <blockquote v-if="userIntentQuote" class="starter-pack-intent">
            <v-icon icon="mdi-format-quote-open" size="16" />
            <span>{{ userIntentQuote }}</span>
          </blockquote>
        </header>

        <div v-if="starterPackLoading" class="starter-pack-loading" data-testid="studio-starter-pack-loading">
          <v-progress-circular indeterminate color="primary" size="22" width="3" />
          <span>{{ t('analysis.studio.starterPack.loading') }}</span>
        </div>
        <div
          v-else-if="starterPackError"
          class="starter-pack-error"
          role="alert"
          data-testid="studio-starter-pack-error"
        >
          <v-icon icon="mdi-alert-circle-outline" size="20" />
          <div class="starter-pack-error-text">
            <strong>{{ t('analysis.studio.starterPack.errorTitle') }}</strong>
            <span>{{ t('analysis.studio.starterPack.errorBody') }}</span>
          </div>
          <v-btn
            variant="flat"
            color="primary"
            size="small"
            data-testid="studio-starter-pack-retry"
            @click="$emit('retry-starter-pack')"
          >
            <v-icon icon="mdi-refresh" start />
            {{ t('analysis.studio.starterPack.retry') }}
          </v-btn>
        </div>
        <!-- Derivation + command bar share one flex flow. The command bar is
             rendered ONCE (no per-breakpoint markup duplication); on mobile it
             is promoted above the rationale via CSS `order` on the `--mobile`
             modifier, on desktop it sits below in source order. -->
        <div class="starter-pack-flow">
          <div
            v-if="!starterPackLoading && starterPackDerivation"
            class="starter-pack-derivation-wrap"
            :class="{ 'starter-pack-derivation-wrap--clamped': derivationClampable && !derivationExpanded }"
          >
            <p
              class="starter-pack-derivation"
              data-testid="studio-starter-pack-derivation"
            >
              {{ starterPackDerivation }}
            </p>
            <button
              v-if="derivationClampable"
              type="button"
              class="starter-pack-derivation-toggle"
              data-testid="studio-starter-pack-derivation-toggle"
              @click="derivationExpanded = !derivationExpanded"
            >
              <v-icon :icon="derivationExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'" size="16" />
              {{ derivationExpanded ? t('analysis.studio.derivationCollapse') : t('analysis.studio.derivationExpand') }}
            </button>
          </div>

          <div
            ref="customCommandRef"
            class="starter-pack-custom"
            :class="{ 'starter-pack-custom--mobile': isMobileStudio }"
            data-testid="studio-custom-command"
          >
            <div class="starter-pack-custom-label">
              <v-icon icon="mdi-pencil-outline" size="16" />
              <span>{{ t('analysis.studio.starterPack.customLabel') }}</span>
            </div>
            <v-textarea
              v-model="freeText"
              :placeholder="t('analysis.studio.commandPlaceholder')"
              :disabled="!hasPremiumAccess || treeBusy || treeCapStatus.blocked"
              variant="outlined"
              density="comfortable"
              rows="2"
              auto-grow
              hide-details
              class="starter-pack-textarea"
            />
            <v-tooltip v-if="hasPremiumAccess" :disabled="!treeCapStatus.blocked" :text="treeCapTooltip" location="top">
              <template #activator="{ props: activator }">
                <span v-bind="activator" v-if="hasPremiumAccess" class="starter-pack-custom-submit">
                  <v-btn
                    color="primary"
                    variant="flat"
                    :block="isMobileStudio"
                    :disabled="!hasPremiumAccess || !freeText.trim() || treeBusy || treeCapStatus.blocked"
                    :loading="treeBusy && lastSubmittedSource === 'free_text'"
                    data-testid="studio-free-text-submit"
                    @click="submitFreeText"
                  >
                    <v-icon icon="mdi-auto-fix" start />
                    {{ t('analysis.studio.commandSubmit', { count: hub.studioStepCredits }) }}
                  </v-btn>
                </span>
              </template>
            </v-tooltip>
            <v-btn
              v-else
              variant="text"
              color="primary"
              class="starter-pack-custom-unlock"
              :block="isMobileStudio"
              @click="$emit('unlock', { kind: 'free_text', suggestionId: null })"
            >
              {{ t('analysis.studio.paywallCta') }}
            </v-btn>
          </div>
        </div>

        <h4 v-if="suggestions.length" class="starter-pack-subtitle">{{ t('analysis.studio.suggestionsTitle') }}</h4>
        <div v-if="suggestions.length" class="suggestion-grid">
          <article
            v-for="suggestion in suggestions"
            :key="suggestion.id"
            :ref="(el) => registerSuggestionRef(suggestion.id, el)"
            :data-suggestion-id="suggestion.id"
            :data-suggestion-archetype="suggestion.archetype"
            class="studio-suggestion"
          >
            <div
              class="suggestion-icon-tile"
              :class="`tile-${suggestion.archetype}`"
              aria-hidden="true"
            >
              <v-icon :icon="suggestion.archetypeIcon || 'mdi-image-edit-outline'" size="48" />
            </div>
            <div class="suggestion-content">
              <div class="suggestion-meta">
                <span>{{ suggestion.archetypeLabel || suggestion.archetype }}</span>
                <span>
                  <v-icon icon="mdi-circle-multiple" size="14" />
                  {{ hub.studioStepCredits }}
                </span>
              </div>
              <h4>{{ suggestion.title }}</h4>
              <v-tooltip
                location="top"
                open-delay="350"
                max-width="420"
                :text="`${suggestion.title}. ${suggestion.description}`"
              >
                <template #activator="{ props: tipProps }">
                  <p v-bind="tipProps" class="suggestion-description">{{ suggestion.description }}</p>
                </template>
              </v-tooltip>
              <v-tooltip
                :disabled="!(hasPremiumAccess && treeCapStatus.blocked)"
                :text="treeCapTooltip"
                location="top"
              >
                <template #activator="{ props: activator }">
                  <span v-bind="activator" style="display:block;">
                    <v-btn
                      color="primary"
                      variant="flat"
                      block
                      :data-testid="`studio-execute-${suggestion.archetype}`"
                      :disabled="hasPremiumAccess && (treeBusy || treeCapStatus.blocked)"
                      :loading="hasPremiumAccess && treeBusy && lastSubmittedSuggestionId === suggestion.id"
                      @click="submitSuggestion(suggestion)"
                    >
                      <v-icon icon="mdi-auto-fix" start />
                      {{ t(hasPremiumAccess ? 'analysis.studio.execute' : 'analysis.studio.paywallCta') }}
                    </v-btn>
                  </span>
                </template>
              </v-tooltip>
            </div>
          </article>
        </div>

      </section>
    </div>

    <OnboardingTour
      tour-id="studio"
      :version="1"
      :ready="Boolean(hub)"
      :auto-show="false"
      :steps="studioTourSteps"
    />
  </section>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useDisplay } from 'vuetify';
import { useI18n } from 'vue-i18n';
import OnboardingTour from '@/components/onboarding/OnboardingTour.vue';

const studioTourSteps = [
  { icon: 'mdi-auto-fix', tone: 'studio', titleKey: 'onboarding.studio.step1.title', bodyKey: 'onboarding.studio.step1.body' },
  { icon: 'mdi-format-list-checks', tone: 'warm', titleKey: 'onboarding.studio.step2.title', bodyKey: 'onboarding.studio.step2.body' },
  { icon: 'mdi-call-split', tone: 'primary', titleKey: 'onboarding.studio.step3.title', bodyKey: 'onboarding.studio.step3.body' },
  { icon: 'mdi-file-pdf-box', tone: 'success', titleKey: 'onboarding.studio.step4.title', bodyKey: 'onboarding.studio.step4.body' },
];

const props = defineProps({
  hub: { type: Object, required: true },
  suggestions: { type: Array, required: true },
  imageUrl: { type: String, default: '' },
  analysisId: { type: String, default: 'current' },
  hasPremiumAccess: { type: Boolean, default: false },
  userIntent: { type: Object, default: null },
  // Optional AI-generated rationale text that ties the suggestions below
  // to the user's stated goal. Empty/loading states are handled by
  // `starterPackLoading` and a graceful absence of the paragraph.
  starterPackDerivation: { type: String, default: '' },
  starterPackLoading: { type: Boolean, default: false },
  starterPackError: { type: Boolean, default: false },
  nodes: { type: Array, default: () => [] },
  currentNodeId: { type: String, default: null },
  pendingGeneration: { type: Object, default: null },
  treeBusy: { type: Boolean, default: false },
  driftHintVisible: { type: Boolean, default: false },
  stubMode: { type: Boolean, default: false },
  initialFreeText: { type: String, default: '' },
  exportBusy: { type: Boolean, default: false },
  menuDriven: { type: Boolean, default: true },
});

const userIntentQuote = computed(() => {
  const text = (props.userIntent?.expectations || '').trim();
  return text || '';
});

const emit = defineEmits([
  'unlock',
  'suggestion-seen',
  'generate',
  'select-node',
  'dismiss-drift',
  'cancel-generation',
  'export-branch',
  'retry-starter-pack',
]);

const { t } = useI18n();
const display = useDisplay();
const isMobileStudio = computed(() => display.smAndDown.value);

const freeText = ref('');
const customCommandRef = ref(null);
let lastSeededPrefill = '';

// Mobile: the AI-generated "Kurzfazit" derivation paragraph is often
// a dense 6-8 line block that pushes the suggestion CTAs below the
// fold. We clamp it on mobile when long and let users expand on demand.
const derivationExpanded = ref(false);
const DERIVATION_CLAMP_CHARS = 220;
const derivationClampable = computed(() => {
  if (!isMobileStudio.value) return false;
  const text = props.starterPackDerivation || '';
  return text.length > DERIVATION_CLAMP_CHARS;
});
watch(
  () => props.starterPackDerivation,
  () => { derivationExpanded.value = false; },
);

async function revealPrefilledCommandBar() {
  await nextTick();
  customCommandRef.value?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  customCommandRef.value?.querySelector('textarea')?.focus();
}

// v2 spec §3.3 — the sidebar tree defaults to the "linear ancestry of
// current + immediate children" view; users can toggle to the full
// depth-sorted list for advanced navigation.
const showFullTree = ref(false);

// A new ?prefill=... in the URL should always overwrite the command-bar
// draft (so re-clicking a 360 "Im Studio iterieren →" chip re-seeds the
// box). We track the last-seeded value to avoid clobbering user input
// when the same prefill is fed back on hot-reload.
watch(
  () => props.initialFreeText,
  (value) => {
    if (!value) return;
    if (value === lastSeededPrefill) return;
    lastSeededPrefill = value;
    freeText.value = value;
    void revealPrefilledCommandBar();
  },
  { immediate: true },
);
const lastSubmittedSource = ref(null);
const lastSubmittedSuggestionId = ref(null);

const suggestionRefs = new Map();
const seenSuggestions = new Set();
let suggestionObserver = null;

const ensureSuggestionObserver = () => {
  if (suggestionObserver || typeof IntersectionObserver === 'undefined') return;
  suggestionObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const el = entry.target;
      const id = el.getAttribute('data-suggestion-id');
      const archetype = el.getAttribute('data-suggestion-archetype');
      if (!id || seenSuggestions.has(id)) return;
      seenSuggestions.add(id);
      emit('suggestion-seen', { suggestionId: id, archetype });
    });
  }, { threshold: 0.4 });
};

const registerSuggestionRef = (id, el) => {
  ensureSuggestionObserver();
  const prev = suggestionRefs.get(id);
  if (prev && prev !== el && suggestionObserver) suggestionObserver.unobserve(prev);
  if (el) {
    suggestionRefs.set(id, el);
    suggestionObserver?.observe(el);
  } else {
    suggestionRefs.delete(id);
  }
};

watch(
  () => props.suggestions.map((suggestion) => suggestion.id).join('|'),
  () => { seenSuggestions.clear(); },
);

watch(
  () => [props.currentNodeId, isMobileStudio.value],
  () => {
    // Reset the condensed/full tree density to the compact default whenever
    // the active node or breakpoint changes.
    if (isMobileStudio.value) showFullTree.value = false;
  },
);

onMounted(() => { ensureSuggestionObserver(); });

onBeforeUnmount(() => {
  if (suggestionObserver) {
    suggestionObserver.disconnect();
    suggestionObserver = null;
  }
  suggestionRefs.clear();
  seenSuggestions.clear();
});

const orderedNodes = computed(() => {
  const list = [...props.nodes];
  list.sort((a, b) => {
    const ad = a.depth ?? 0;
    const bd = b.depth ?? 0;
    if (ad !== bd) return ad - bd;
    return String(a.createdAt || '').localeCompare(String(b.createdAt || ''));
  });
  return list;
});

const activeNode = computed(() => {
  if (!props.currentNodeId) return null;
  return orderedNodes.value.find((node) => node.id === props.currentNodeId) || null;
});
const rootNode = computed(() => orderedNodes.value.find((node) => node.parentNodeId === null) || null);
const currentWorkspaceNode = computed(() => activeNode.value || rootNode.value || null);

// On mobile, when sitting on the root node with no generated step yet
// and no in-flight generation, the empty "AKTUELLES KONZEPT" placeholder
// pane wastes a whole half of the concept stage. Collapse to a single
// pane in that case so the user sees the original plan at full width
// and reaches the prompt composer faster. Desktop keeps the two-pane
// layout because it has the horizontal room.
// The mobile workspace strip headline already shows the current node
// title. The line below it should give *new* information — either a
// reminder that no step has been generated yet (root), how deep the
// current step is on the path, or "+N concepts available" once the
// tree has multiple nodes.
const workspaceSubtitle = computed(() => {
  if (orderedNodes.value.length > 1) {
    return t('analysis.studio.nav.expandTree', { count: orderedNodes.value.length });
  }
  const node = currentWorkspaceNode.value;
  if (!node || !node.parentNodeId) {
    return t('analysis.studio.nav.rootSubtitle');
  }
  return t('analysis.studio.nav.iterationSubtitle', { depth: (node.depth || 0) + 1 });
});

const singleConceptPane = computed(() => {
  if (!isMobileStudio.value) return false;
  if (props.pendingGeneration) return false;
  if (activeNode.value && activeNode.value.parentNodeId) return false;
  return true;
});

// The hero greets first-time visitors with "Start auf Basis Ihrer Anforderungen".
// Once they're past the original plan (a real generated step is current), it
// would be stale to keep promising "start" — so the copy switches to an
// iteration variant that frames the next action as a refinement of the
// existing concept.
const isOnIteratedNode = computed(
  () => Boolean(activeNode.value) && activeNode.value.parentNodeId !== null,
);
const heroEyebrow = computed(
  () => t(isOnIteratedNode.value ? 'analysis.studio.iterationEyebrow' : 'analysis.studio.eyebrow'),
);
const heroTitle = computed(
  () => t(isOnIteratedNode.value ? 'analysis.studio.iterationTitle' : 'analysis.studio.title'),
);
const heroLede = computed(
  () => t(isOnIteratedNode.value ? 'analysis.studio.iterationLede' : 'analysis.studio.heroLede'),
);

/**
 * Build the condensed hierarchical view for the sidebar:
 *   - ancestors of the current node (root → ... → parent)
 *   - the current node itself (highlighted)
 *   - immediate children of the current node, each tagged with a
 *     "+N" chip if it has further descendants we're not showing
 *   - siblings of the current node so the user can branch laterally
 *
 * Returns `{ condensed: bool, entries: [{ node, indent, role, siblingCount }] }`.
 * `condensed=false` means the tree is small enough to render flat
 * regardless of the toggle.
 */
const treeDisplay = computed(() => {
  const nodes = orderedNodes.value;
  if (nodes.length === 0) return { condensed: false, entries: [] };
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const childrenOf = new Map();
  nodes.forEach((n) => {
    if (!n.parentNodeId) return;
    const arr = childrenOf.get(n.parentNodeId) || [];
    arr.push(n);
    childrenOf.set(n.parentNodeId, arr);
  });

  // Tiny trees (≤ 6 nodes) fit fine flat — skip the condensed treatment.
  if (nodes.length <= 6) {
    return {
      condensed: false,
      entries: nodes.map((node) => ({
        node,
        indent: Math.min(node.depth || 0, 5),
        role: node.id === props.currentNodeId ? 'current' : node.parentNodeId === null ? 'root' : 'flat',
        siblingCount: 0,
      })),
    };
  }

  const current = props.currentNodeId ? byId.get(props.currentNodeId) : null;
  if (!current) {
    return {
      condensed: true,
      entries: nodes.map((node) => ({
        node,
        indent: Math.min(node.depth || 0, 5),
        role: node.parentNodeId === null ? 'root' : 'flat',
        siblingCount: 0,
      })),
    };
  }

  const ancestors = [];
  let cursor = current;
  while (cursor && cursor.parentNodeId) {
    const parent = byId.get(cursor.parentNodeId);
    if (!parent) break;
    ancestors.unshift(parent);
    cursor = parent;
  }

  const entries = [];
  ancestors.forEach((node) => {
    entries.push({
      node,
      indent: Math.min(node.depth || 0, 5),
      role: node.parentNodeId === null ? 'root' : 'ancestor',
      siblingCount: 0,
    });
  });

  entries.push({
    node: current,
    indent: Math.min(current.depth || 0, 5),
    role: 'current',
    siblingCount: 0,
  });

  // Siblings of current (so the user can move laterally without expanding the full tree).
  if (current.parentNodeId) {
    const siblings = (childrenOf.get(current.parentNodeId) || []).filter((n) => n.id !== current.id);
    siblings.forEach((node) => {
      const descendants = countDescendants(node, childrenOf);
      entries.push({
        node,
        indent: Math.min(current.depth || 0, 5),
        role: 'sibling',
        siblingCount: descendants,
      });
    });
  }

  // Immediate children of current (one level only — toggle reveals the rest).
  const children = childrenOf.get(current.id) || [];
  children.forEach((node) => {
    const descendants = countDescendants(node, childrenOf);
    entries.push({
      node,
      indent: Math.min((current.depth || 0) + 1, 5),
      role: 'child',
      siblingCount: descendants,
    });
  });

  return { condensed: true, entries };
});

function countDescendants(node, childrenOf) {
  const stack = [node];
  let count = 0;
  while (stack.length) {
    const top = stack.pop();
    const kids = childrenOf.get(top.id) || [];
    count += kids.length;
    kids.forEach((kid) => stack.push(kid));
  }
  return count;
}

// Tree caps mirror the backend (v2 spec §4.2). Surface as disabled state
// + tooltip on the Execute controls so users see why an action is blocked
// before they spend a credit.
const TREE_CAP_NODES = 20;
const TREE_CAP_DEPTH = 8;
const TREE_CAP_WIDTH = 5;

const treeCapStatus = computed(() => {
  const total = Math.max(0, orderedNodes.value.length - 1); // exclude root
  const parent = activeNode.value;
  const parentId = parent ? parent.id : null;
  const siblings = parentId
    ? orderedNodes.value.filter((n) => n.parentNodeId === parentId).length
    : 0;
  const depth = parent ? (parent.depth || 0) + 1 : 1;
  if (total >= TREE_CAP_NODES) return { blocked: true, reason: 'tree' };
  if (depth > TREE_CAP_DEPTH) return { blocked: true, reason: 'depth' };
  if (siblings >= TREE_CAP_WIDTH) return { blocked: true, reason: 'width' };
  return { blocked: false, reason: null };
});

const treeCapTooltip = computed(() => {
  if (!treeCapStatus.value.blocked) return '';
  if (treeCapStatus.value.reason === 'tree') return t('analysis.studio.caps.tree', { max: TREE_CAP_NODES });
  if (treeCapStatus.value.reason === 'depth') return t('analysis.studio.caps.depth', { max: TREE_CAP_DEPTH });
  if (treeCapStatus.value.reason === 'width') return t('analysis.studio.caps.width', { max: TREE_CAP_WIDTH });
  return '';
});

// Export-branch action requires at least one step beyond the root.
const canExportBranch = computed(() => Boolean(activeNode.value && activeNode.value.parentNodeId));

const cumulativeDirectives = computed(() => {
  if (!activeNode.value) return [];
  const byId = new Map(orderedNodes.value.map((node) => [node.id, node]));
  const chain = [];
  let cursor = activeNode.value;
  while (cursor && cursor.parentNodeId) {
    chain.unshift(cursor.directive);
    cursor = byId.get(cursor.parentNodeId);
  }
  return chain;
});

// The caption rendered below the original/concept pair. Prefer the
// pending directive (so the user sees what they just submitted while the
// spinner runs) and fall back to the active node's directive once it
// resolves. Returns an empty string when there is nothing meaningful to
// show (no concept generated yet, root node), which suppresses the
// caption block entirely.
const conceptCaption = computed(() => {
  if (props.pendingGeneration?.directive) return props.pendingGeneration.directive;
  if (activeNode.value?.parentNodeId && activeNode.value?.directive) {
    return activeNode.value.directive;
  }
  return '';
});

// The horizontal "trail" of past directives leading to the current
// concept. The active step is the last one in the list (the caption
// already shows its directive in full, so the trail only repeats it for
// breadcrumb continuity — kept short via CSS line-clamp).
const conceptTrail = computed(() => {
  const chain = cumulativeDirectives.value;
  return chain.slice(0, -1);
});

// Smoothly scroll the concept stage to the top of the viewport whenever
// the user fires a generate so they actually see the spinner replace the
// placeholder image. Behaviour is conservative: only scrolls *up* (never
// down), and uses smooth behaviour where supported.
const conceptStageRef = ref(null);
function scrollToConceptStage() {
  const el = conceptStageRef.value;
  if (!el || typeof el.getBoundingClientRect !== 'function') return;
  const rect = el.getBoundingClientRect();
  if (rect.top < 0 || rect.top > 120) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

function nodeLabel(node) {
  if (!node.parentNodeId) return t('analysis.studio.nav.rootLabel');
  if (typeof node.directive === 'string' && node.directive.trim()) {
    return node.directive.length > 56 ? `${node.directive.slice(0, 56).trim()}…` : node.directive;
  }
  return t('analysis.studio.nav.unnamed');
}

function archetypeLabel(archetype) {
  if (!archetype) return '';
  const key = `analysis.studio.archetype.${archetype}`;
  return t(key);
}

function onSelectNode(node) {
  if (!node) return;
  if (node.id === props.currentNodeId) return;
  emit('select-node', { nodeId: node.id });
}

function submitFreeText() {
  const directive = freeText.value.trim();
  if (!props.hasPremiumAccess) {
    emit('unlock', {
      kind: 'free_text',
      directive,
      directiveSource: 'free_text',
      suggestionId: null,
      archetype: null,
    });
    return;
  }
  if (!directive || props.treeBusy) return;
  lastSubmittedSource.value = 'free_text';
  lastSubmittedSuggestionId.value = null;
  emit('generate', {
    directive,
    directiveSource: 'free_text',
    archetype: null,
    suggestionId: null,
    parentNodeId: props.currentNodeId,
  });
  freeText.value = '';
  scrollToConceptStage();
}

function submitSuggestion(suggestion) {
  const directive = suggestion.description?.length
    ? `${suggestion.title}. ${suggestion.description}`
    : suggestion.title;
  if (!props.hasPremiumAccess) {
    emit('unlock', {
      kind: 'execute',
      directive,
      directiveSource: 'starter_suggestion',
      suggestionId: suggestion.id,
      archetype: suggestion.archetype,
    });
    return;
  }
  if (props.treeBusy) return;
  lastSubmittedSource.value = 'starter_suggestion';
  lastSubmittedSuggestionId.value = suggestion.id;
  emit('generate', {
    directive,
    directiveSource: 'starter_suggestion',
    archetype: suggestion.archetype,
    suggestionId: suggestion.id,
    parentNodeId: props.currentNodeId,
  });
  scrollToConceptStage();
}
</script>

<style scoped>
.studio-dashboard {
  display: grid;
  grid-template-columns: minmax(220px, 260px) minmax(0, 1fr);
  gap: 14px;
  align-items: start;
}

/* When the parent view is using the unified AnalysisSideMenu the internal
 * Studio sidebar duplicates navigation. Hide it and let the main pane
 * take the full width. */
.studio-dashboard--menu-driven {
  grid-template-columns: minmax(0, 1fr);
}
.studio-dashboard--menu-driven > .studio-side {
  display: none;
}

.studio-side {
  position: sticky;
  top: 86px;
  display: grid;
  gap: 12px;
  padding: 12px;
  border: 1px solid rgba(41, 70, 86, 0.14);
  border-radius: 8px;
  background: #ffffff;
}

.studio-side-title {
  padding: 4px 8px 8px;
  color: #55717c;
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
}

.studio-side-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.studio-side-summary {
  display: grid;
  gap: 4px;
  min-width: 0;
}

.studio-side-current-title {
  color: #172b33;
  font-size: 1rem;
  line-height: 1.3;
}

.studio-side-current-meta {
  color: #55717c;
  font-size: 0.8rem;
  line-height: 1.35;
}

.studio-tree-expand {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 34px;
  padding: 7px 10px;
  border: 1px solid rgba(41, 70, 86, 0.12);
  border-radius: 999px;
  background: #f7fbfc;
  color: #245346;
  font: inherit;
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
}

.node-list,
.node-actions {
  display: grid;
  gap: 4px;
}

.node-item,
.node-action {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 42px;
  padding: 9px 10px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #263f48;
  font: inherit;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
}

.node-item[disabled],
.node-action[disabled] {
  opacity: 0.55;
  cursor: not-allowed;
}

.node-item {
  position: relative;
}

.node-item--active {
  background: #edf4f1;
  color: #245346;
  font-weight: 700;
  box-shadow: inset 3px 0 0 #245346;
}

.node-item--current {
  background: #edf4f1;
  color: #245346;
  font-weight: 700;
  box-shadow: inset 3px 0 0 #245346;
}

.node-item--ancestor {
  color: #4a6770;
}

.node-item--sibling {
  color: #76532a;
}

.node-item--child {
  color: #36505a;
  border-left: 1px dashed rgba(36, 83, 70, 0.35);
}

.node-item--root .node-thumb {
  background: #f5ead9;
}

.node-connector {
  position: absolute;
  left: 5px;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgba(36, 83, 70, 0.2);
  pointer-events: none;
}

.node-sibling-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 18px;
  padding: 0 6px;
  border-radius: 999px;
  background: rgba(36, 83, 70, 0.12);
  color: #245346;
  font-size: 0.7rem;
  font-weight: 700;
}

.node-tree-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  border: 0;
  padding: 6px 10px;
  border-radius: 6px;
  background: transparent;
  color: #245346;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
}

.node-tree-toggle:hover {
  background: rgba(36, 83, 70, 0.08);
}

.node-thumb {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 28px;
  flex: 0 0 36px;
  overflow: hidden;
  border-radius: 4px;
  background: #edf2f4;
}

.node-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.node-label {
  display: grid;
  gap: 2px;
  min-width: 0;
}

.node-title {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
}

.node-meta {
  color: #55717c;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.node-empty {
  padding: 8px 10px;
  color: #55717c;
  font-size: 0.85rem;
}

.node-action--accent {
  color: #5d4423;
  background: #f5ead9;
  font-weight: 700;
}

.node-action--inline {
  margin-top: 6px;
}

.branch-pending {
  margin: 4px 0 0;
  padding: 8px 10px;
  border-radius: 6px;
  background: #edf4f1;
  color: #245346;
  font-size: 0.8rem;
}

.studio-main {
  min-width: 0;
  display: grid;
  gap: 16px;
  overflow-x: clip;
}

.studio-hero {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  padding: 22px;
  border: 1px solid rgba(41, 70, 86, 0.14);
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 14px 36px rgba(24, 39, 48, 0.07);
}

.eyebrow {
  margin: 0 0 5px;
  color: #55717c;
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
}

.studio-hero h2 {
  margin: 0 0 8px;
  color: #172b33;
  font-size: 1.7rem;
}

.studio-hero p {
  margin: 0;
}

.studio-credit-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 34px;
  padding: 7px 11px;
  border-radius: 999px;
  background: #f5ead9;
  color: #76532a;
  font-weight: 800;
  white-space: nowrap;
}

.drift-hint {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid rgba(196, 132, 18, 0.32);
  border-radius: 8px;
  background: #fff7e6;
  color: #6b4912;
}

.stub-banner {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  align-self: start;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(36, 83, 70, 0.12);
  color: #245346;
  font-size: 0.85rem;
  font-weight: 700;
}

/* ────────────────────────────────────────────────────────────────────
   Concept stage — balanced original/current pair with shared caption
   The old layout had the directive paragraph hanging off the right
   column alone, which made the two image panes height-mismatched and
   looked unintentional. The new layout treats the pair as one figure:
   equal column widths, equal frame aspect ratios, and a single
   spanning caption + trail breadcrumb below.
   ──────────────────────────────────────────────────────────────────── */

.concept-stage {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px;
  background: #ffffff;
  border: 1px solid rgba(41, 70, 86, 0.10);
  border-radius: 16px;
  box-shadow: 0 18px 38px rgba(24, 39, 48, 0.05);
}

.concept-stage-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

/* When we collapse the empty "current concept" placeholder on mobile
   root, the original plan can spread to the full row width. */
.concept-stage--single .concept-stage-grid {
  grid-template-columns: 1fr;
}

.concept-pane {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.concept-pane-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: #55717c;
  font-size: 0.74rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.concept-pane-label-main {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.concept-pane-archetype {
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(34, 81, 92, 0.08);
  color: #22515c;
  font-size: 0.7rem;
  letter-spacing: 0.04em;
}

.concept-frame {
  aspect-ratio: 4 / 3;
  overflow: hidden;
  border: 1px solid rgba(41, 70, 86, 0.12);
  border-radius: 12px;
  background: #f5f8f9;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.concept-pane--current .concept-frame {
  /* Slight emphasis so the user's eye lands here first. */
  border-color: rgba(34, 81, 92, 0.22);
  box-shadow: 0 8px 22px rgba(24, 39, 48, 0.05);
}

.concept-frame img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #ffffff;
}

.concept-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 18px;
  color: #78909a;
  text-align: center;
}

.concept-empty--placeholder p {
  margin: 0;
  font-size: 0.92rem;
  max-width: 220px;
  line-height: 1.4;
}

.concept-generating {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 18px;
  color: #2d4350;
  font-weight: 600;
  font-size: 0.92rem;
}

.concept-generating-eta {
  color: #55717c;
  font-weight: 400;
  font-size: 0.85rem;
  max-width: 240px;
  text-align: center;
  line-height: 1.4;
}

/* Spans both columns. Replaces the previous orange .current-directive
   block which hung off the right column only. */
.concept-stage-caption {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0;
  padding: 12px 14px;
  background: #f3f7f9;
  border-left: 3px solid #4a8d9e;
  border-radius: 8px;
  color: #2d4350;
  font-size: 0.95rem;
  line-height: 1.5;
}

.concept-stage-caption :deep(.v-icon) {
  flex-shrink: 0;
  color: #4a8d9e;
  margin-top: 2px;
}

.concept-stage-trail {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 12px;
  padding-top: 6px;
}

.concept-trail-label {
  color: #55717c;
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.concept-trail-steps {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
  min-width: 0;
}

.concept-trail-step {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 260px;
  padding: 4px 10px 4px 4px;
  border: 1px solid rgba(41, 70, 86, 0.14);
  border-radius: 999px;
  background: #fdfefe;
  color: #2d4350;
  font-size: 0.82rem;
  line-height: 1.3;
}

.concept-trail-step + .concept-trail-step::before {
  content: '→';
  margin-right: -2px;
  color: #8c9fa6;
  font-weight: 700;
}

.concept-trail-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #22515c;
  color: #ffffff;
  font-size: 0.72rem;
  font-weight: 700;
  flex-shrink: 0;
}

.concept-trail-text {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

@media (max-width: 720px) {
  .concept-stage {
    padding: 14px;
  }
  .concept-stage-grid {
    grid-template-columns: 1fr;
  }
  .concept-trail-step {
    max-width: 100%;
  }
}

.command-bar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 10px;
  padding: 14px;
  border: 1px solid rgba(41, 70, 86, 0.14);
  border-radius: 8px;
  background: #ffffff;
}

.cap-warning {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 8px 12px;
  border-radius: 6px;
  background: #fff7e6;
  color: #6b4912;
  font-size: 0.85rem;
}

.studio-mobile-bar {
  display: none;
}

@media (max-width: 600px) {
  /* The floating ContactButton is intentionally hidden on the mobile
     Studio route (see Analysis.vue), so the sticky bar can run the
     full content width without leaving a 70px corner gutter for it. */
  .studio-mobile-bar {
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 5;
    box-sizing: border-box;
    max-width: 100%;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 8px 14px;
    background: #172b33;
    color: #ffffff;
    font-weight: 700;
    box-shadow: 0 -8px 20px rgba(23, 43, 51, 0.18);
  }
  .studio-mobile-bar-pill {
    display: inline-flex;
    align-items: center;
    flex: 0 0 auto;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 999px;
    background: rgba(245, 234, 217, 0.14);
    font-size: 0.85rem;
  }
  .studio-mobile-bar-cost {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    flex: 0 0 auto;
    color: #f5ead9;
    font-size: 0.78rem;
    font-weight: 500;
    line-height: 1.2;
    opacity: 0.85;
  }
}

.section-label {
  margin-bottom: 10px;
  color: #55717c;
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
}

/* Unified "starter pack" — replaces the previously separate "Ihre
   Anforderungen" panel + top free-text command bar. One coherent flow:
   kicker + intent quote + derivation paragraph + suggestion grid +
   inline custom-command fallback. */
.starter-pack {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 20px;
  padding: 24px;
  overflow-x: clip;
  background: linear-gradient(180deg, #f5fafb 0%, #ffffff 100%);
  border: 1px solid rgba(41, 70, 86, 0.10);
  border-radius: 16px;
}

.starter-pack-header {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.starter-pack-kicker {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #55717c;
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.starter-pack-title {
  margin: 0;
  color: #172b33;
  font-size: 1.4rem;
  font-weight: 700;
  line-height: 1.25;
}

.starter-pack-intent {
  margin: 4px 0 0;
  padding: 10px 14px;
  display: flex;
  min-width: 0;
  gap: 8px;
  align-items: flex-start;
  background: rgba(245, 234, 217, 0.55);
  border-left: 3px solid #d6a76a;
  border-radius: 6px;
  color: #4a3d2a;
  font-style: italic;
  font-size: 0.95rem;
  line-height: 1.45;
}

.starter-pack-intent :deep(.v-icon) {
  color: #b08454;
  margin-top: 2px;
  flex-shrink: 0;
}

.starter-pack-intent span {
  min-width: 0;
  overflow-wrap: anywhere;
}

.starter-pack-derivation-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}

.starter-pack-derivation {
  margin: 0;
  color: #2d4350;
  font-size: 1rem;
  line-height: 1.6;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.starter-pack-derivation-wrap--clamped .starter-pack-derivation {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
  line-clamp: 4;
  overflow: hidden;
}

.starter-pack-derivation-toggle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 0;
  border: 0;
  background: transparent;
  color: #22515c;
  font: inherit;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
}

.starter-pack-derivation-toggle:hover {
  text-decoration: underline;
}

.starter-pack-loading {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #55717c;
  font-size: 0.95rem;
}

.starter-pack-error {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid rgba(179, 80, 65, 0.32);
  border-radius: 8px;
  background: #fdf3f1;
  color: #8a3624;
}

.starter-pack-error :deep(.v-icon) {
  color: #b35041;
  flex-shrink: 0;
  margin-top: 2px;
}

.starter-pack-error-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1 1 auto;
  font-size: 0.92rem;
  line-height: 1.4;
}

.starter-pack-error-text strong {
  color: #6b2718;
  font-weight: 700;
}

@media (max-width: 600px) {
  .starter-pack-error {
    flex-direction: column;
    align-items: stretch;
  }
}

.starter-pack-subtitle {
  margin: 4px 0 0;
  color: #2d4350;
  font-size: 1.05rem;
  font-weight: 700;
}

/* Holds the rationale + the single command bar. Matches the parent
   `.starter-pack` 20px column rhythm so collapsing the two former
   per-breakpoint blocks into one flow keeps the original spacing. */
.starter-pack-flow {
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
}

.starter-pack-custom {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas:
    "label label"
    "input button";
  gap: 10px;
  margin-top: 8px;
  min-width: 0;
  padding-top: 20px;
  border-top: 1px dashed rgba(41, 70, 86, 0.18);
}

.starter-pack-custom--mobile {
  /* Promote the command bar above the rationale within `.starter-pack-flow`
     on mobile, and drop the separator since it now sits at the top. */
  order: -1;
  margin-top: 0;
  padding-top: 0;
  border-top: 0;
}

.starter-pack-custom-label {
  grid-area: label;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #55717c;
  font-size: 0.85rem;
  font-weight: 600;
}

.starter-pack-textarea {
  grid-area: input;
  min-width: 0;
  width: 100%;
}

.starter-pack-custom-submit {
  grid-area: button;
  align-self: end;
  display: inline-flex;
  min-width: 0;
}

.starter-pack-custom-unlock {
  grid-area: button;
  align-self: end;
  min-width: 0;
}

@media (max-width: 720px) {
  .starter-pack {
    padding: 18px;
    gap: 16px;
  }
  .starter-pack-custom {
    grid-template-columns: 1fr;
    grid-template-areas:
      "label"
      "input"
      "button";
  }
  .starter-pack-custom-submit,
  .starter-pack-custom-unlock {
    width: 100%;
  }
}

.studio-requirements-context :deep(.user-query-card) {
  height: auto;
}

.suggestion-column h3 {
  margin: 0 0 12px;
  color: #172b33;
}

.suggestion-column {
  min-width: 0;
}

.suggestion-grid {
  display: grid;
  min-width: 0;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 14px;
}

.studio-suggestion {
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  border: 1px solid rgba(41, 70, 86, 0.14);
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 12px 32px rgba(24, 39, 48, 0.06);
}

/* Tinted archetype tile replaces the previous static SVG previews. The
   colour pairs (background + icon) act as a category badge — no pretence
   of being a generated floor-plan rendering. */
.suggestion-icon-tile {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  aspect-ratio: 16 / 10;
  background: linear-gradient(135deg, #eef5f7 0%, #d6e6ec 100%);
  color: #22515c;
  border-bottom: 1px solid rgba(41, 70, 86, 0.08);
}

.suggestion-icon-tile :deep(.v-icon) {
  opacity: 0.78;
}

.suggestion-icon-tile.tile-add_room {
  background: linear-gradient(135deg, #e8f3e6 0%, #c9e0c4 100%);
  color: #2f6b3a;
}

.suggestion-icon-tile.tile-merge_rooms {
  background: linear-gradient(135deg, #fbe9d6 0%, #f1c79b 100%);
  color: #8a4a14;
}

.suggestion-icon-tile.tile-open_wall {
  background: linear-gradient(135deg, #eef5f7 0%, #b8d4dd 100%);
  color: #1f4a55;
}

.suggestion-icon-tile.tile-relocate_entrance {
  background: linear-gradient(135deg, #f1ecfb 0%, #cdbef0 100%);
  color: #4a378a;
}

.suggestion-icon-tile.tile-redesign_kitchen {
  background: linear-gradient(135deg, #fdecec 0%, #f1b2ab 100%);
  color: #8a2a22;
}

.suggestion-icon-tile.tile-3d_vignette {
  background: linear-gradient(135deg, #e7eefb 0%, #b3c4ea 100%);
  color: #1f3a72;
}


.suggestion-content {
  display: grid;
  flex: 1;
  grid-template-rows: auto auto minmax(0, 1fr) auto;
  gap: 10px;
  min-height: 286px;
  padding: 14px;
}

.suggestion-meta {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  color: #55717c;
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
}

.suggestion-meta span:last-child {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.studio-suggestion h4,
.studio-suggestion p {
  margin: 0;
}

.studio-suggestion h4 {
  color: #172b33;
  font-size: 1rem;
  line-height: 1.25;
}

.studio-suggestion p {
  display: -webkit-box;
  overflow: hidden;
  color: #5a4c43;
  line-height: 1.5;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 7;
}

.suggestion-description {
  cursor: help;
}

/* Bottom-align the execute button across cards. The :deep selector reaches
   the v-btn that Vuetify nests inside the v-tooltip activator span. */
.studio-suggestion :deep(.v-btn) {
  align-self: end;
  margin-top: auto;
}

@media (max-width: 920px) {
  .studio-dashboard,
  .studio-workspace {
    grid-template-columns: 1fr;
  }

  .studio-side {
    position: static;
    overflow-x: visible;
  }

  .command-bar {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .studio-hero {
    display: grid;
    gap: 12px;
    padding: 16px;
  }

  .studio-hero h2 {
    margin-bottom: 6px;
    font-size: 1.32rem;
    overflow-wrap: anywhere;
  }

  .studio-hero-lede {
    font-size: 0.94rem;
    line-height: 1.45;
  }

  .studio-credit-pill {
    min-height: 30px;
    width: fit-content;
    padding: 6px 10px;
    font-size: 0.84rem;
  }

  .studio-side {
    gap: 10px;
    padding: 12px;
    border-radius: 14px;
  }

  .studio-side-title {
    padding: 0;
    font-size: 0.72rem;
  }

  .studio-side-current-title {
    font-size: 0.94rem;
  }

  .studio-tree-expand {
    max-width: 46%;
    white-space: normal;
    text-align: left;
  }

  .node-list {
    gap: 6px;
    max-height: 320px;
    overflow-y: auto;
    padding-right: 4px;
  }

  .node-item {
    min-height: 40px;
    padding: 8px 10px;
  }

  .node-connector { display: none; }

  .node-actions {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px;
  }

  .node-action {
    min-width: 0;
    justify-content: flex-start;
    gap: 6px;
    min-height: 38px;
    padding: 8px 10px;
    font-size: 0.8rem;
    line-height: 1.25;
  }

  .node-action span {
    overflow-wrap: anywhere;
  }

  .branch-pending {
    margin-top: 0;
    font-size: 0.78rem;
  }

  /* Generating overlay takes the whole workspace pane so the user has
     a clear, focused indicator on a small screen. */
  .image-frame {
    aspect-ratio: 4 / 3;
  }

  /* Studio suggestions stack on mobile. The previous clipped carousel
     looked like a layout bug because the next card was visibly cut off. */
  .suggestion-grid {
    display: grid;
    inline-size: 100%;
    width: 100%;
    max-width: 100%;
    grid-template-columns: 1fr;
    overflow-x: visible;
    overflow-y: visible;
    gap: 10px;
    padding-right: 0;
    padding-bottom: 0;
  }

  .studio-suggestion {
    width: 100%;
    max-width: 100%;
  }

  .suggestion-icon-tile {
    aspect-ratio: 16 / 7;
  }

  .suggestion-content {
    min-height: 0;
    gap: 8px;
    padding: 12px 12px 16px;
  }

  .studio-suggestion p {
    -webkit-line-clamp: 4;
  }

  .starter-pack {
    gap: 14px;
    padding: 16px;
  }

  .starter-pack-title {
    font-size: 1.2rem;
  }

  .starter-pack-intent,
  .starter-pack-derivation {
    font-size: 0.92rem;
    line-height: 1.5;
  }

  .starter-pack-subtitle {
    margin-top: 2px;
    font-size: 0.98rem;
  }

  .starter-pack-custom-label {
    font-size: 0.8rem;
  }

  .concept-stage {
    gap: 12px;
    padding: 14px;
  }

  .concept-stage-caption {
    padding: 10px 12px;
    font-size: 0.9rem;
  }

  .concept-stage-trail {
    gap: 8px;
  }

  .studio-main {
    gap: 14px;
    /* Sticky bottom credit bar (~44px) + breathing room so the last
       suggestion CTA never sits underneath it. */
    padding-bottom: 72px;
  }
}
</style>
